import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

const CallbackEventForm = ({ eventData, mode = 'view', onClose, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    id: '',
    event_name: '',
    display_name: '',
    description: '',
    category: '',
    severity: 'medium',
    is_curated: false,
    is_active: true,
    usage_count: 0,
    last_triggered: null,
    context_data_schema: {},
    sample_context_data: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize form data from eventData prop
  useEffect(() => {
    if (eventData) {
      setFormData({
        id: eventData.id || '',
        event_name: eventData.event_name || '',
        display_name: eventData.display_name || '',
        description: eventData.description || '',
        category: eventData.category || '',
        severity: eventData.severity || 'medium',
        is_curated: eventData.is_curated || false,
        is_active: eventData.is_active !== undefined ? eventData.is_active : true,
        usage_count: eventData.usage_count || 0,
        last_triggered: eventData.last_triggered || null,
        context_data_schema: eventData.context_data_schema || {},
        sample_context_data: eventData.sample_context_data || {}
      });
    }
  }, [eventData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleJsonChange = (field, value) => {
    try {
      const parsedValue = JSON.parse(value);
      setFormData(prev => ({
        ...prev,
        [field]: parsedValue
      }));
      setError(null);
    } catch (err) {
      setError(`Invalid JSON in ${field}: ${err.message}`);
    }
  };

  const handleSave = async () => {
    setLoading(true); setError(null); setSuccess(null);
    try {
      const response = await adminApi.put(`/admin/callback/system/core/events/manage/${formData.id}/`, formData);
      if (response.data && response.data.success) {
        setSuccess('Event updated successfully!');
        setTimeout(() => {
          onSaveSuccess && onSaveSuccess();
          onClose();
        }, 1500);
      } else {
        setError('Failed to save event data');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving event data');
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = mode === 'view';

  return (
    <div className="callback-event-form">
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}
      <div className="row">
        <div className="col-md-6">
          <div className="mb-3">
            <label htmlFor="event_name" className="form-label">Event Name</label>
            <input
              type="text"
              className="form-control"
              id="event_name"
              name="event_name"
              value={formData.event_name}
              onChange={handleInputChange}
              readOnly={isReadOnly}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="mb-3">
            <label htmlFor="display_name" className="form-label">Display Name</label>
            <input
              type="text"
              className="form-control"
              id="display_name"
              name="display_name"
              value={formData.display_name}
              onChange={handleInputChange}
              readOnly={isReadOnly}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="mb-3">
            <label htmlFor="category" className="form-label">Category</label>
            <input
              type="text"
              className="form-control"
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              readOnly={isReadOnly}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="mb-3">
            <label htmlFor="severity" className="form-label">Severity</label>
            <select
              className="form-select"
              id="severity"
              name="severity"
              value={formData.severity}
              onChange={handleInputChange}
              disabled={isReadOnly}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="description" className="form-label">Description</label>
        <textarea
          className="form-control"
          id="description"
          name="description"
          rows="3"
          value={formData.description}
          onChange={handleInputChange}
          readOnly={isReadOnly}
        />
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="mb-3">
            <label htmlFor="usage_count" className="form-label">Usage Count</label>
            <input
              type="number"
              className="form-control"
              id="usage_count"
              name="usage_count"
              value={formData.usage_count}
              onChange={handleInputChange}
              readOnly={isReadOnly}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="mb-3">
            <label htmlFor="last_triggered" className="form-label">Last Triggered</label>
            <input
              type="datetime-local"
              className="form-control"
              id="last_triggered"
              name="last_triggered"
              value={formData.last_triggered ? new Date(formData.last_triggered).toISOString().slice(0, 16) : ''}
              onChange={handleInputChange}
              readOnly={isReadOnly}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="mb-3 d-flex align-items-center">
            <div className="form-check me-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                disabled={isReadOnly}
              />
              <label className="form-check-label" htmlFor="is_active">
                Active
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="is_curated"
                name="is_curated"
                checked={formData.is_curated}
                onChange={handleInputChange}
                disabled={isReadOnly}
              />
              <label className="form-check-label" htmlFor="is_curated">
                Curated
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="context_data_schema" className="form-label">Context Data Schema</label>
        <textarea
          className="form-control font-monospace"
          id="context_data_schema"
          rows="8"
          value={JSON.stringify(formData.context_data_schema, null, 2)}
          onChange={(e) => handleJsonChange('context_data_schema', e.target.value)}
          readOnly={isReadOnly}
          style={{ fontSize: '0.875rem' }}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="sample_context_data" className="form-label">Sample Context Data</label>
        <textarea
          className="form-control font-monospace"
          id="sample_context_data"
          rows="8"
          value={JSON.stringify(formData.sample_context_data, null, 2)}
          onChange={(e) => handleJsonChange('sample_context_data', e.target.value)}
          readOnly={isReadOnly}
          style={{ fontSize: '0.875rem' }}
        />
      </div>

      <div className="d-flex gap-2">
        {!isReadOnly && (
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Event'}
          </button>
        )}
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {isReadOnly ? 'Close' : 'Cancel'}
        </button>
      </div>
    </div>
  );
};

export default CallbackEventForm;